import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { AuthLayout } from '@/components/layout/auth-layout';
import { useAuthStore } from '@/lib/auth-store';

function extractApiMessage(err: unknown, fallback: string): string {
  const body = (err as { body?: unknown })?.body;
  const raw = (body as { message?: unknown })?.message;
  if (typeof raw === 'string') return raw;
  return (err as Error)?.message ?? fallback;
}

/**
 * Estado intermediario entre signIn (mfaRequired) e sessao ativa.
 * Le mfaPending da auth store. Se nao houver, redireciona para sign-in.
 */
export default function MfaVerifyPage() {
  const navigate = useNavigate();
  const mfaPending = useAuthStore((s) => s.mfaPending);
  const verifyMfa = useAuthStore((s) => s.verifyMfa);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!mfaPending) {
    return <Navigate to="/sign-in" replace />;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await verifyMfa(code);
      const state = useAuthStore.getState();
      if (state.isSuperAdmin) {
        navigate('/admin');
      } else if (!state.tenantId) {
        navigate('/sem-escritorio');
      } else {
        navigate('/escritorio');
      }
    } catch (err) {
      setError(extractApiMessage(err, 'Código inválido'));
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthLayout>
      <Card>
        <CardHeader>
          <CardTitle>Verificação em duas etapas</CardTitle>
          <CardDescription>
            Insira o código de 6 dígitos do seu app autenticador.
          </CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="code">Código</Label>
              <Input
                id="code"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                required
              />
              <p className="text-xs text-[var(--fg-muted)]">
                Sem o app? Use um dos seus códigos de recuperação.
              </p>
            </div>
            {error && <p className="text-sm text-red-600">{error}</p>}
          </CardContent>
          <CardFooter>
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? 'Verificando...' : 'Verificar'}
            </Button>
          </CardFooter>
        </form>
      </Card>
    </AuthLayout>
  );
}
